import { message, Form, Button, Drawer } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { useState, useMemo } from 'react';
import { providerService } from 'services';
import FormItem from 'components/FormItem';
import { useAsync } from 'react-async-hook';
import MaskedDisplay from 'components/ui/MaskedDisplay';
import Select from 'components/Select';
import TaxProfileForm from 'components/TaxProfileForm';

export function TaxDetails({ careFacility, setCareFacility, isEditable = false }) {
  const [form] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [orgProfiles, setOrgProfiles] = useState([]);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [creatingProfile, setCreatingProfile] = useState(false);

  useAsync(async () => {
    if (careFacility?.organization?.id === undefined) return;
    try {
      const profiles = await providerService.getTaxProfiles(careFacility.organization.id);
      setOrgProfiles(profiles || []);
    } catch (error) {
      console.log('Failed to fetch tax profiles', error);
    }
  }, [careFacility?.organization?.id]);

  const { isEditing, editProps } = useEditCard({
    id: 'tax-details',
    onEditStart: () => {
      const activeProfile = orgProfiles.find((p) => p.id === careFacility?.taxProfile?.id);
      form.setFieldsValue({ taxProfileId: activeProfile?.id || null });
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();

      if (!values.taxProfileId) {
        message.error('Please select a tax profile');
        throw new Error('No tax profile selected');
      }

      await providerService.activateTaxProfileOnFacility(careFacility.id, values.taxProfileId);

      const updatedCareFacility = await providerService.getCenterById(Number(careFacility?.id));
      setCareFacility(updatedCareFacility);
      form.resetFields();
      message.success('Tax details updated');
    },
    onDiscard: () => form.resetFields(),
  });

  const selectedProfileId = Form.useWatch('taxProfileId', form);
  const selectedProfile = useMemo(
    () => orgProfiles.find((p) => p.id === selectedProfileId),
    [orgProfiles, selectedProfileId],
  );

  const activeProfile = useMemo(
    () => orgProfiles.find((p) => p.id === careFacility?.taxProfile?.id),
    [orgProfiles, careFacility?.taxProfile?.id],
  );

  const profileOptions = useMemo(
    () =>
      orgProfiles.map((profile) => ({
        id: profile.id,
        title: profile.profileName || 'Unnamed Profile',
        subtitle: profile.taxClassification?.title || '',
      })),
    [orgProfiles],
  );

  const handleCreateNewProfile = async (values) => {
    try {
      setCreatingProfile(true);

      const dto = {
        profileName: values.profileName,
        taxIdNumber: values.taxIdNumber,
        taxClassification: values.taxClassification ?? null,
      };

      const newProfile = await providerService.createTaxProfile(careFacility.organization.id, dto);

      message.success('Tax profile created successfully');

      const profiles = await providerService.getTaxProfiles(careFacility.organization.id);
      setOrgProfiles(profiles || []);

      setShowCreateDrawer(false);
      profileForm.resetFields();

      form.setFieldsValue({ taxProfileId: newProfile.id });
    } catch (error) {
      console.error('Error creating tax profile:', error);
      message.error(error.response?.data?.message || 'Failed to create tax profile');
    } finally {
      setCreatingProfile(false);
    }
  };

  const displayProfile = isEditing ? selectedProfile : activeProfile;

  return (
    <>
      <Card
        className="mt-8"
        title="Tax details"
        extra={isEditable ? <CardEdit {...editProps} /> : null}
        noBodyPadding
        stickyHeader
      >
        <Form form={form} disabled={editProps.loading}>
          <TabularList
            indented
            data={[
              {
                title: 'Tax Profile',
                titleRequired: true,
                value: isEditing ? (
                  <div className="flex flex-col gap-2 max-w-lg">
                    {orgProfiles.length > 0 && (
                      <FormItem
                        name="taxProfileId"
                        rules={[{ required: true, message: 'Tax profile is required' }]}
                        className="mb-0"
                      >
                        <Select
                          placeholder="Select a tax profile"
                          options={profileOptions}
                          formatOptionLabel={(opt, { context }) => {
                            if (context === 'value') return opt.title;
                            return (
                              <div>
                                <div>{opt.title}</div>
                                <div className="text-xs text-gray-500">{opt.subtitle}</div>
                              </div>
                            );
                          }}
                          isOptionSelected={(option, values) => {
                            const valueId = Array.isArray(values) ? values[0] : values;
                            return option.id === valueId;
                          }}
                          onChangeFormatter={(option) => option?.id}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </FormItem>
                    )}

                    <Button
                      type="link"
                      onClick={() => {
                        profileForm.resetFields();
                        setShowCreateDrawer(true);
                      }}
                      className="p-0"
                      style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      + Create New Profile
                    </Button>
                  </div>
                ) : activeProfile ? (
                  activeProfile.profileName || 'Unnamed Profile'
                ) : (
                  'No profile selected'
                ),
              },
              ...(displayProfile
                ? [
                    {
                      title: 'Tax classification',
                      value: displayProfile.taxClassification?.title || '-',
                    },
                    {
                      title: 'Tax ID',
                      value: displayProfile.taxIdNumber ? <MaskedDisplay value={displayProfile.taxIdNumber} /> : '-',
                    },
                  ]
                : []),
            ]}
          />
        </Form>
      </Card>

      <Drawer
        title="Create New Tax Profile"
        placement="right"
        onClose={() => {
          setShowCreateDrawer(false);
          profileForm.resetFields();
        }}
        open={showCreateDrawer}
        width={640}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              size="large"
              onClick={() => {
                setShowCreateDrawer(false);
                profileForm.resetFields();
              }}
              disabled={creatingProfile}
            >
              Cancel
            </Button>
            <Button type="primary" size="large" onClick={() => profileForm.submit()} loading={creatingProfile}>
              Save profile
            </Button>
          </div>
        }
      >
        <Form form={profileForm} onFinish={handleCreateNewProfile} layout="vertical">
          <TaxProfileForm />
        </Form>
      </Drawer>
    </>
  );
}
